import style from './style.module.scss';
import ChildBlock from '../child_block/template';
import EditableFields from '../editable_fields/';
import { getPermissionsByBlockType, humanizedBlockType } from '../helpers';
import OrderableListItem from '../orderable_list_item';
import { ContentBlock, ContentBlockEditorData, ContentBlockTypes } from '../types';
import { DangerButton, PrimaryButton } from '@magoosh/lib/button';
import cx from 'classnames';
import { adminPaths } from 'config/path_helpers';
import * as React from 'react';
import fetch from 'utilities/fetch';
import { FormikHelpers as FormikActions } from 'formik';
import ErrorBoundary from '@magoosh/utils/error_boundary';

interface State {
  block: ContentBlock;
  isLoading: boolean;
}

export default class ParentBlock extends React.Component<ContentBlockEditorData, State> {
  state = {
    block: this.props.contentBlock,
    isLoading: false
  };

  componentDidMount() {
    // This page dynamically loads content blocks via async requests, so the browser's history is
    // manually maintained by storing the content block ids when navigation occurs. This stores the
    // initial content block id.
    history.replaceState(
      { contentBlockId: this.props.contentBlock.id },
      `Content Block ${this.props.contentBlock.id}`,
      adminPaths.editContentBlock(this.props.contentBlock.id)
    );

    window.addEventListener('popstate', this.handlePopState);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handlePopState);
  }

  handlePopState = (event) => {
    // Handles the browser having gone back a page, and loads the content block with the id stored
    // in the state history.
    this.loadBlockById(event.state.contentBlockId, false);
  };

  render() {
    const { block, isLoading } = this.state;

    return (
      <div className={cx(style.container, { [style.loading]: isLoading })}>
        <a href={adminPaths.contentBlocks}>All Content Blocks</a>
        {this.state.block.ancestors.map((ancestor, idx) => (
          <div key={ancestor.id}>
            {'>'.repeat(idx + 1)}{' '}
            {this.state.block.id === ancestor.id ? (
              <span>
                {humanizedBlockType(ancestor.type)} #{ancestor.id}: {ancestor.name}
              </span>
            ) : (
              <a onClick={() => this.loadBlockById(ancestor.id)}>
                {humanizedBlockType(ancestor.type)} #{ancestor.id}: {ancestor.name}
              </a>
            )}
          </div>
        ))}
        <EditableFields config={this.props.config} block={block} key={block.id} />

        <ErrorBoundary>{this.renderChildBlocks()}</ErrorBoundary>
        <hr />

        <DangerButton onClick={this.deleteBlock}>Delete this block</DangerButton>
      </div>
    );
  }

  /**
   * UI to add, remove, and arrange child blocks
   */
  renderChildBlocks() {
    const block = this.state.block;
    const childBlocks = block.childBlocks || [];
    const permissions = getPermissionsByBlockType(block.type);

    if (!permissions.manageChildBlocks) {
      return null;
    }

    return (
      <React.Fragment>
        <hr />
        <i>Immediate Children ({childBlocks.length}):</i>

        {childBlocks.map((c) => (
          <OrderableListItem
            key={c.id}
            onMoveDown={() => this.moveBlock(c.id, 'down')}
            onMoveUp={() => this.moveBlock(c.id, 'up')}>
            <ChildBlock {...c} onEdit={() => this.loadBlockById(c.id)} />
          </OrderableListItem>
        ))}

        <div className="u-margin-T-m">
          {permissions.addAssignmentBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::AssignmentBlock')}>
              Add Assignment Block
            </PrimaryButton>
          )}
          {permissions.addSectionedAssignmentBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::SectionedAssignmentBlock')}>
              Add Sectioned Assignment Block
            </PrimaryButton>
          )}
          {permissions.addLessonChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::LessonBlock')}>
              Add Lesson
            </PrimaryButton>
          )}
          {permissions.addPracticeSessionChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::PracticeSessionBlock')}>
              Add Practice Session / Quiz
            </PrimaryButton>
          )}
          {permissions.addJsonBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::JSONBlock')}>
              Add JSON Payload
            </PrimaryButton>
          )}
          {permissions.addContainerBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::Container')}>
              Add Container
            </PrimaryButton>
          )}
          {permissions.addLearningSessionBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::LearningSessionBlock')}>
              Add Learning Session
            </PrimaryButton>
          )}
          {permissions.addReviewBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::ReviewBlock')}>
              Add Review
            </PrimaryButton>
          )}
          {permissions.addLessonSeriesLinkBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::LessonSeriesLinkBlock')}>
              Add Lesson Series
            </PrimaryButton>
          )}
          {permissions.addExternalResourceBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::ExternalResourceBlock')}>
              Add External Resource
            </PrimaryButton>
          )}
          {permissions.addGreSubsectionBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::SubsectionBlock')}>
              Add Subsection
            </PrimaryButton>
          )}
          {permissions.addGreSegmentBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::SegmentBlock')}>
              Add Segment
            </PrimaryButton>
          )}
          {permissions.addGreSliceBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::SliceBlock')}>
              Add Slice
            </PrimaryButton>
          )}
          {permissions.addGreSlicePracticeBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::SlicePracticeBlock')}>
              Add Practice
            </PrimaryButton>
          )}
          {permissions.addGreAdaptiveSlicePracticeBlockChild && (
            <PrimaryButton
              onClick={() => this.createChildBlock('ContentBlock::GRE::AdaptiveSlicePracticeBlock')}>
              Add Adaptive Practice (GAP v2)
            </PrimaryButton>
          )}
          {permissions.addGreExercisePracticeBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::ExercisePracticeBlock')}>
              Add Exercises
            </PrimaryButton>
          )}
          {permissions.addGreDrillPracticeBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::DrillPracticeBlock')}>
              Add Drills
            </PrimaryButton>
          )}
          {permissions.addGreConceptPracticeBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::ConceptPracticeBlock')}>
              Add Concepts
            </PrimaryButton>
          )}
          {permissions.addGreTestlikePracticeBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::TestLikePracticeBlock')}>
              Add Test-Like questions
            </PrimaryButton>
          )}
          {permissions.addDrillBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::DrillBlock')}>
              Add Drill
            </PrimaryButton>
          )}
          {permissions.addGreSegmentLinkBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::SegmentLinkBlock')}>
              Add Segment Link
            </PrimaryButton>
          )}
          {permissions.addGreMixedPracticeLinkBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::GRE::MixedPracticeLinkBlock')}>
              Add Mixed Practice Link
            </PrimaryButton>
          )}
          {permissions.addStudyScheduleBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::StudyScheduleBlock')}>
              Add Study Schedule
            </PrimaryButton>
          )}
          {permissions.addFlashcardsDeckBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::FlashcardsDeckBlock')}>
              Add Flashcard Deck
            </PrimaryButton>
          )}
          {permissions.addRichTextBlockChild && (
            <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::RichTextBlock')}>
              Add Rich Text Block
            </PrimaryButton>
          )}
          {this.renderAddLessonSeriesButton(block)}
        </div>
      </React.Fragment>
    );
  }

  renderAddLessonSeriesButton = (block) => {
    // Fow now, Lesson Series blocks are only supported in a particular structure of content blocks
    // for MCAT Content Review.
    if (block.ancestors.length !== 3 || block.ancestors[0].name !== 'MCAT Content Review') {
      return null;
    }

    return (
      <PrimaryButton onClick={() => this.createChildBlock('ContentBlock::LessonSeriesBlock')}>
        Add Lesson Series
      </PrimaryButton>
    );
  };

  loadBlockById = (id, trackHistory = true) => {
    this.fetchThenSetBlock(adminPaths.api.showContentBlock(id)).then(() => {
      if (trackHistory) {
        history.pushState({ contentBlockId: id }, `Content Block ${id}`, adminPaths.editContentBlock(id));
      }
    });
  };

  moveBlock = (id, direction: 'up' | 'down') => {
    this.fetchThenSetBlock(adminPaths.api.moveContentBlock(id), {
      method: 'PATCH',
      body: JSON.stringify({ direction })
    });
  };

  createChildBlock = (type: ContentBlockTypes) => {
    this.fetchThenSetBlock(adminPaths.api.createContentBlock(), {
      method: 'POST',
      body: JSON.stringify({
        type,
        parentId: this.state.block.id
      })
    });
  };

  deleteBlock = () => {
    this.fetchThenSetBlock(adminPaths.api.destroyContentBlock(this.state.block.id), {
      method: 'DELETE'
    });
  };

  fetchThenSetBlock = (path, config = undefined, actions: FormikActions<ContentBlock> = null) => {
    this.setState({ isLoading: true });

    return fetch(path, config)
      .then((b) => {
        this.setState({ block: b, isLoading: false });
      })
      .catch((e) => {
        console.error(e);
        alert('Something went wrong! Try again or reach out to Eng for support.');
        this.setState({ isLoading: false });
      });
  };
}
